import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { KeyRound, Plus, Trash2, Cloud, Smartphone, Check } from 'lucide-react';
import { startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { passkeysAPI, PasskeyDevice } from '../../services/api';

/**
 * Gestion de passkeys en el perfil: alta, listado y borrado.
 *
 * Una passkey sustituye a la contraseña: se guarda en el llavero de iCloud o en
 * el gestor de contraseñas de Google y se desbloquea con Face ID, Touch ID o la
 * huella. La clave privada nunca sale del dispositivo del usuario.
 */
export const PasskeysSection: React.FC = () => {
  const [devices, setDevices] = useState<PasskeyDevice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRegistering, setIsRegistering] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const supported = browserSupportsWebAuthn();

  const loadDevices = useCallback(async () => {
    try {
      const response = await passkeysAPI.getDevices();
      if (response.success && response.data) {
        setDevices(response.data.devices);
      }
    } catch {
      // Silencioso: si no se pueden cargar, se muestra la seccion vacia
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (supported) {
      loadDevices();
    } else {
      setIsLoading(false);
    }
  }, [supported, loadDevices]);

  const handleRegister = async () => {
    setError('');
    setSuccess('');
    setIsRegistering(true);

    try {
      const optionsResponse = await passkeysAPI.registerBegin();
      if (!optionsResponse.success || !optionsResponse.data) {
        throw new Error(optionsResponse.message || 'No se pudo iniciar el registro');
      }

      // Aqui salta el dialogo del sistema (Face ID / Touch ID / huella)
      const credential = await startRegistration({ optionsJSON: optionsResponse.data });

      // Nombre orientativo del dispositivo para que el usuario distinga sus passkeys
      const deviceName = /iPhone|iPad|Android/i.test(navigator.userAgent)
        ? 'Teléfono'
        : 'Ordenador';

      const result = await passkeysAPI.registerComplete(credential, deviceName);
      if (!result.success) {
        throw new Error(result.message || 'No se pudo guardar la passkey');
      }

      setSuccess('Passkey creada correctamente');
      await loadDevices();
    } catch (err: any) {
      // Cancelar el dialogo del sistema no es un error que merezca aviso
      const cancelled = err?.name === 'NotAllowedError' || err?.name === 'AbortError';
      if (!cancelled) {
        setError(err?.message || 'No se pudo crear la passkey');
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const handleDelete = async (id: number) => {
    setError('');
    setSuccess('');
    try {
      const response = await passkeysAPI.deleteDevice(id);
      if (response.success) {
        setDevices((prev) => prev.filter((d) => d.id !== id));
      } else {
        setError(response.message || 'No se pudo eliminar');
      }
    } catch {
      setError('No se pudo eliminar la passkey');
    }
  };

  // Si el navegador no soporta WebAuthn no se enseña nada: no hay accion posible
  if (!supported) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.18 }}
      className="mb-6"
    >
      <h3 className="text-sm font-medium text-[var(--color-text-muted)] mb-3 px-1">
        Acceso rápido
      </h3>

      <div className="card p-4">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[var(--color-accent-soft)] flex-shrink-0">
            <KeyRound size={20} className="text-[var(--color-accent)]" />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-[var(--color-text)]">Passkeys</p>
            <p className="text-sm text-[var(--color-text-muted)]">
              Entra con Face ID, Touch ID o tu huella, sin escribir la contraseña.
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="py-4 flex justify-center">
            <div className="w-5 h-5 border-2 border-[var(--color-accent)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {devices.length > 0 && (
              <div className="space-y-2 mb-4">
                {devices.map((device) => (
                  <div
                    key={device.id}
                    className="flex items-center gap-3 p-3 rounded-xl bg-[var(--color-bg)]"
                  >
                    <Smartphone size={18} className="text-[var(--color-text-muted)] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[var(--color-text)] truncate">
                        {device.deviceName}
                      </p>
                      <p className="text-xs text-[var(--color-text-muted)] flex items-center gap-1">
                        {device.syncedToCloud && (
                          <>
                            <Cloud size={11} />
                            Sincronizada
                            <span className="mx-1">·</span>
                          </>
                        )}
                        {new Date(device.createdAt).toLocaleDateString('es-ES')}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDelete(device.id)}
                      className="p-2 rounded-lg text-[var(--color-text-muted)] hover:text-red-500 hover:bg-red-500/10 transition-colors flex-shrink-0"
                      aria-label={`Eliminar ${device.deviceName}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <AnimatePresence>
              {error && (
                <motion.p
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="text-sm text-red-500 mb-3"
                >
                  {error}
                </motion.p>
              )}
              {success && (
                <motion.p
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="text-sm text-green-500 mb-3 flex items-center gap-1"
                >
                  <Check size={14} />
                  {success}
                </motion.p>
              )}
            </AnimatePresence>

            <button
              onClick={handleRegister}
              disabled={isRegistering}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 border-dashed border-[var(--color-border)] text-[var(--color-text-muted)] font-medium hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] transition-colors disabled:opacity-60"
            >
              {isRegistering ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <Plus size={18} />
              )}
              {devices.length === 0 ? 'Crear passkey' : 'Añadir otra passkey'}
            </button>
          </>
        )}
      </div>
    </motion.div>
  );
};
